/**
 * The brand's "scribble" layer (Brand Guidelines v1.0, pages 13 to 15): a
 * candid photo marked up as if a teacher went over it with a marker.
 *
 * Marks are drawn in ONE svg over the photo, in the photo's own pixel space
 * (default 1600x900), with `preserveAspectRatio="xMidYMid slice"`. That is the
 * same centered crop `object-cover` gives the photo, so a circle aimed at a
 * phone stays on the phone at every width and in every card aspect ratio.
 *
 * Paths come from a seeded random, so the server and browser draw the same
 * wobble and there is no hydration mismatch.
 *
 * Restraint rule: two or three marks per image plus the logo; asterisks don't
 * count. Keep circles off the main subject's face (a phone at their ear counts);
 * a secondary person in the background is fine. Yellow is for marks only, never text. Caveat is for notes on images
 * only, never body copy, buttons or headings.
 */

import Image from 'next/image'
import type { ReactNode } from 'react'

import { cn } from '@/lib/utils'

import { handFont } from './fonts'

export const MARKER_YELLOW = '#FFC933'
const SHADOW_NAVY = '#0F3D5F'

function seeded(seed: number) {
  let s = seed
  const rnd = () => {
    s = (s * 16807) % 2147483647
    return (s - 1) / 2147483646
  }
  return { rnd, jitter: (a: number) => (rnd() * 2 - 1) * a }
}

type Point = [number, number]

// Every path number is rounded: the server and the browser can disagree in the last float
// digit of Math.cos/sin, which makes React log a hydration mismatch.
const r1 = (n: number) => n.toFixed(1)
const pt = (p: Point) => `${r1(p[0])} ${r1(p[1])}`

function smooth(pts: Point[]) {
  let d = `M${pts[0][0].toFixed(1)} ${pts[0][1].toFixed(1)}`
  for (let i = 1; i < pts.length - 1; i++) {
    const xc = (pts[i][0] + pts[i + 1][0]) / 2
    const yc = (pts[i][1] + pts[i + 1][1]) / 2
    d += ` Q${pts[i][0].toFixed(1)} ${pts[i][1].toFixed(1)} ${xc.toFixed(1)} ${yc.toFixed(1)}`
  }
  const last = pts[pts.length - 1]
  return `${d} L${last[0].toFixed(1)} ${last[1].toFixed(1)}`
}

const strokeProps = (color: string, width: number) =>
  ({
    fill: 'none',
    stroke: color,
    strokeWidth: width,
    strokeLinecap: 'round',
    strokeLinejoin: 'round'
  }) as const

/** A loose marker loop that overshoots about 1.18 turns with a slight tilt. */
export function MarkerCircle({
  cx,
  cy,
  rx,
  ry,
  rot = -8,
  width = 10,
  seed = 7,
  color = MARKER_YELLOW
}: {
  cx: number
  cy: number
  rx: number
  ry: number
  rot?: number
  width?: number
  seed?: number
  color?: string
}) {
  const { jitter } = seeded(seed)
  const pts: Point[] = []
  const start = jitter(0.6) - 1.9
  const r = (rot * Math.PI) / 180
  const n = 46
  for (let i = 0; i <= n; i++) {
    const t = start + (1.18 * 2 * Math.PI * i) / n
    const drift = 1 + 0.07 * (i / n) + jitter(0.018)
    const x = Math.cos(t) * rx * drift
    const y = Math.sin(t) * ry * drift
    pts.push([cx + x * Math.cos(r) - y * Math.sin(r), cy + x * Math.sin(r) + y * Math.cos(r)])
  }
  return <path d={smooth(pts)} {...strokeProps(color, width)} filter="url(#sj-mark-shadow)" />
}

/** The marker circle an arrow points at: the same values passed to `MarkerCircle`. */
type CircleTarget = { cx: number; cy: number; rx: number; ry: number; rot?: number }

/**
 * Where the curve first reaches the circle, plus a gap. The loop drifts to
 * about 1.1x its radius, so the edge is inflated by that before the gap.
 */
function stopBefore(target: CircleTarget, gap: number, at: (t: number) => Point) {
  const r = ((target.rot ?? -8) * Math.PI) / 180
  const rx = target.rx * 1.1 + gap
  const ry = target.ry * 1.1 + gap
  for (let i = 0; i <= 200; i++) {
    const t = i / 200
    const [x, y] = at(t)
    const dx = x - target.cx
    const dy = y - target.cy
    const u = dx * Math.cos(r) + dy * Math.sin(r)
    const v = -dx * Math.sin(r) + dy * Math.cos(r)
    if ((u / rx) ** 2 + (v / ry) ** 2 <= 1) return t
  }
  return 1
}

/**
 * An arrow from a note to the circled thing. Route it around people, never
 * across them. Always pass `target` (the circle's values) when it points at a
 * circle: the arrow then stops just short of it, the house rule, so the head
 * never touches or enters the loop. Short arrows draw as one smooth curve.
 */
export function ScribbleArrow({
  from,
  via,
  to,
  target,
  gap = 22,
  width = 8,
  head = 26,
  seed = 3,
  color = MARKER_YELLOW,
  wobble
}: {
  from: Point
  via: Point
  to: Point
  target?: CircleTarget
  gap?: number
  width?: number
  head?: number
  seed?: number
  color?: string
  /**
   * Hand wobble along the line. Short arrows (under about 320 units) default to
   * none: jitter on a short line reads as wonky, not hand-drawn. Longer arrows get
   * a gentle wobble that grows with length. Matches the blog image renderer.
   */
  wobble?: number
}) {
  const { jitter } = seeded(seed)
  const len = Math.hypot(to[0] - from[0], to[1] - from[1])
  const wob = wobble ?? (len < 320 ? 0 : 1.6 * Math.min(1, len / 600))
  const at = (t: number): Point => [
    (1 - t) ** 2 * from[0] + 2 * (1 - t) * t * via[0] + t * t * to[0],
    (1 - t) ** 2 * from[1] + 2 * (1 - t) * t * via[1] + t * t * to[1]
  ]
  const end = target ? stopBefore(target, gap, at) : 1
  const pts: Point[] = []
  const n = 24
  for (let i = 0; i <= n; i++) {
    const [x, y] = at((i / n) * end)
    pts.push([x + jitter(wob), y + jitter(wob)])
  }
  const a = pts[n]
  const b = pts[n - 3]
  const ang = Math.atan2(a[1] - b[1], a[0] - b[0])
  const h1: Point = [a[0] - head * Math.cos(ang - 0.5), a[1] - head * Math.sin(ang - 0.5)]
  const h2: Point = [a[0] - head * Math.cos(ang + 0.55), a[1] - head * Math.sin(ang + 0.55)]
  return (
    <g filter="url(#sj-mark-shadow)">
      <path d={smooth(pts)} {...strokeProps(color, width)} />
      <path d={`M${pt(h1)} L${pt(a)} L${pt(h2)}`} {...strokeProps(color, width)} />
    </g>
  )
}

/**
 * Hand asterisks: six points from three slightly curved, uneven strokes, with a
 * dot or two. Always a pair, one large (about 20px) and one small (about 12px),
 * near the note and off text and faces.
 */
export function HandAsterisks({
  x,
  y,
  scale = 1,
  seed = 5,
  color = MARKER_YELLOW
}: {
  x: number
  y: number
  scale?: number
  seed?: number
  color?: string
}) {
  const { rnd, jitter } = seeded(seed)
  const star = (cx: number, cy: number, s: number, w: number) => {
    const strokes = [0, 1, 2].map(k => {
      const a = (k * Math.PI) / 3 + 0.3 + jitter(0.12)
      const len = s * (0.85 + rnd() * 0.3)
      const bend = jitter(s * 0.25)
      const x1 = cx - Math.cos(a) * len
      const y1 = cy - Math.sin(a) * len
      const x2 = cx + Math.cos(a) * len * (0.8 + rnd() * 0.3)
      const y2 = cy + Math.sin(a) * len * (0.8 + rnd() * 0.3)
      const mx = cx - Math.sin(a) * bend
      const my = cy + Math.cos(a) * bend
      return (
        <path key={k} d={`M${r1(x1)} ${r1(y1)} Q${r1(mx)} ${r1(my)} ${r1(x2)} ${r1(y2)}`} {...strokeProps(color, w)} />
      )
    })
    return strokes
  }
  const big = 20 * 1.6 * scale
  const small = 12 * 1.6 * scale
  return (
    <g filter="url(#sj-mark-shadow)">
      {star(x, y, big, 6 * scale)}
      {star(x + big * 2.1, y + big * 1.2, small, 5 * scale)}
      <circle cx={x + big * 1.5} cy={y - big * 0.6} r={4.5 * scale} fill={color} />
      <circle cx={x - big * 0.2} cy={y + big * 1.5} r={3.5 * scale} fill={color} />
    </g>
  )
}

/**
 * A handwritten note: Caveat Bold, two short lowercase lines, tilted -4 to -6
 * degrees. White with a soft shadow on photos and on navy; `ink` (shadow navy,
 * no shadow) on bright backgrounds.
 */
export function HandNote({
  x,
  y,
  lines,
  size = 64,
  rot = -5,
  ink = false
}: {
  x: number
  y: number
  lines: string[]
  size?: number
  rot?: number
  ink?: boolean
}) {
  return (
    <text
      x={x}
      y={y}
      transform={`rotate(${rot} ${x} ${y})`}
      fill={ink ? SHADOW_NAVY : '#FFFFFF'}
      filter={ink ? undefined : 'url(#sj-note-shadow)'}
      style={{ fontFamily: 'var(--font-hand)', fontWeight: 700, fontSize: size }}
    >
      {lines.map((line, i) => (
        <tspan key={line} x={x} dy={i === 0 ? 0 : size * 0.95}>
          {line}
        </tspan>
      ))}
    </text>
  )
}

type Logo = 'horizontal' | 'none'

/**
 * A photo with the scribble layer and the white logo bottom-right (page 15:
 * 210px wide at 1600, 40px from the right, 34px from the bottom).
 */
export function ScribblePhoto({
  src,
  alt,
  width = 1600,
  height = 900,
  className,
  imageClassName,
  sizes = '(min-width: 1024px) 50vw, 100vw',
  priority,
  logo = 'horizontal',
  children
}: {
  src: string
  alt: string
  width?: number
  height?: number
  className?: string
  imageClassName?: string
  sizes?: string
  priority?: boolean
  logo?: Logo
  children?: ReactNode
}) {
  return (
    <div className={cn('@container relative aspect-video overflow-hidden rounded-xl', handFont.variable, className)}>
      <Image
        src={src}
        alt={alt}
        fill
        sizes={sizes}
        priority={priority}
        className={cn('object-cover', imageClassName)}
      />
      {children && (
        <svg
          aria-hidden="true"
          viewBox={`0 0 ${width} ${height}`}
          preserveAspectRatio="xMidYMid slice"
          className="pointer-events-none absolute inset-0 h-full w-full"
        >
          <ScribbleDefs />
          {children}
        </svg>
      )}
      {logo === 'horizontal' && (
        <>
          {/* 13.1% of the width is under 120px below a 916px-wide photo: there, the small-size cut (60 to 120px). */}
          <img
            src="/img/logo/kit/sj-horizontal-small-white.svg"
            alt=""
            className="pointer-events-none absolute right-[2.5cqw] bottom-[2.1cqw] w-[max(64px,13.1cqw)] drop-shadow-[0_1px_4px_rgba(0,0,0,0.35)] @min-[916px]:hidden"
          />
          <img
            src="/img/logo/kit/sj-horizontal-white.svg"
            alt=""
            className="pointer-events-none absolute right-[2.5cqw] bottom-[2.1cqw] hidden w-[13.1cqw] drop-shadow-[0_1px_4px_rgba(0,0,0,0.35)] @min-[916px]:block"
          />
        </>
      )}
    </div>
  )
}

/** Soft shadows: yellow on a bright photo is only 1.5:1, so the shadow is what makes marks readable. */
export function ScribbleDefs() {
  return (
    <defs>
      <filter id="sj-mark-shadow" x="-20%" y="-20%" width="140%" height="140%">
        <feDropShadow dx="0" dy="2" stdDeviation="3" floodColor="#000" floodOpacity="0.35" />
      </filter>
      <filter id="sj-note-shadow" x="-10%" y="-30%" width="120%" height="160%">
        <feDropShadow dx="0" dy="2" stdDeviation="5" floodColor="#000" floodOpacity="0.55" />
      </filter>
    </defs>
  )
}
